import React, {
    useContext, useEffect, useMemo, useState,
} from 'react';
import ContentLoader from 'react-content-loader';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useParams, useSearchParams } from 'react-router-dom';

import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';
import { MovedInReservation } from '@r6digital/rapidstor-types/types/Reservation';

import { useGetCorpQuery, useGetReservationQuery } from '@/core/api/rapidstor';
import Logger from '@/core/utils/logging';

import store from '@/store';
import { setUnitData } from '@/store/bookingSlice';

import Alert from '@/core/components/elements/alert';
import Button from '@/core/components/elements/button';
import WizardDescriptor from '@/core/components/wizard/descriptor';
import MarketingSurvey from '@/core/components/wizard/forms/marketing-survey';
import SimplifiedCheckoutConfirmed from '@/core/components/wizard/simplified-checkout/SimplifiedCheckoutConfirmed';

import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';

import IconClipboardFilled from '@/assets/icon-clipboard-filled.svg?react';
import IconClipboard from '@/assets/icon-clipboard.svg?react';

import classes from '@/scss/screens/move-in.module.scss';

export default function MoveInConfirmed() {
    const location = useLoaderData() as Location;
    const { token } = useParams();
    const { t } = useTranslation();
    const { unitList, rapidstorMainColor } = useContext(JourneyContext);

    const [descriptor, setDescriptor] = useState<Descriptor>();
    const [unitType, setUnitType] = useState<UnitType>();
    const [reservation, setReservation] = useState<MovedInReservation>();
    const [copied, setCopied] = useState<boolean>(false);
    const [searchParams] = useSearchParams();

    const { data: reservationData, isLoading: reservationDataLoading } = useGetReservationQuery(
        {
            locationCode: location.locationCode,
            corpCode: location.corpCode,
            token,
        },
        {
            refetchOnMountOrArgChange: true,
        },
    );

    const { data: corporationData } = useGetCorpQuery(
        {
            corpCode: location.corpCode,
        },
        {
            refetchOnMountOrArgChange: false,
        },
    );

    const [, setCookie, removeCookie] = useCookies(['reservation', 'sessionId']);

    const copyToClipboard = () => {
        if (reservation?.globalWaitingNum) {
            navigator.clipboard.writeText(`${reservation.globalWaitingNum}`);
            setCopied(true);
        }
    };

    const errorAfterMoveIn = useMemo(
        () => reservation?.ledgerResult?.errorAfterMoveIn ||
            (
                searchParams.get('error') === 'true' ?
                searchParams.get('message') :
                ''
            ),
        [reservation, searchParams],
    );

    const handleAfterMoveInErrorMessage = () => {
        if (!errorAfterMoveIn) {
            return '';
        }

        switch (true) {
            case reservation?.ledgerResult?.failedAutobillSetup:
                return t('wizard:bookingConfirmation:autobillError');
            case reservation?.ledgerResult?.failedMerchPayment:
                return t('wizard:bookingConfirmation:merchPaymentError');
            case errorAfterMoveIn === 'Unable to assign insurance policy number':
                return t('wizard:bookingConfirmation:insuranceError');
            default:
                return errorAfterMoveIn;
        }
    };

    const clipBoardIcon = useMemo(() => {
        if (copied) {
            return (
                <IconClipboardFilled />
            );
        }
        return (
            <IconClipboard />
        );
    }, [copied]);

    useEffect(() => {
        const setupSliceData = (nextDescriptor?: Descriptor, nextUnitType?: UnitType) => {
            // Set UnitData
            if (nextDescriptor && nextUnitType && unitList) {
                store.dispatch(
                    setUnitData({
                        location,
                        unitType: nextUnitType,
                        descriptor: nextDescriptor,
                        descriptors: unitList.descriptors,
                    }),
                );
            }
        };

        const checkCookie = (
            nextReservation: MovedInReservation,
            nextDescriptor?: Descriptor,
            nextUnitType?: UnitType,
        ) => {
            // Set Cookie
            setCookie(
                'reservation',
                {
                    token,
                    id: nextReservation._id,
                },
                {
                    path: window.location.pathname,
                    maxAge: 60 * 60 * 24 * 7,
                },
            );
            setupSliceData(nextDescriptor, nextUnitType);
        };

        if (reservationData) {
            /** Esign Redirect */
            if ('finalized' in reservationData && reservationData.finalized) {
                // remove cookie so a user could come back and do another reservation properly
                removeCookie('reservation');
                removeCookie('sessionId');
                if (location.rapidstor.autoESignRedirect && reservationData.ledgerResult?.esign?.url) {
                    window.location.href = reservationData.ledgerResult.esign.url;
                    return;
                }
            }

            const sampleDescriptor: Descriptor = {
                ...reservationData.unitType.descriptor,
                unitTypes: [],
                locationCode: '',
                corpCode: '',
                area: reservationData.unitType.area,
            };

            const sampleUnitType: UnitType = {
                unitTypeId: 0,
                typeName: '',
                deleted: false,
                stdRate: 0,
                stdWeeklyRate: 0,
                hasComputedAvailable: false,
                totalOccupied: 0,
                totalReserved: 0,
                totalUnits: 0,
                totalVacant: 0,
                totalAvailable: 0,
                reservationFee: 0,
                units: [],
                deals: [],
                rates: {
                    ...reservationData.rates,
                    discountedRate: reservationData.rates.discountedRate ?? 0,
                },
                ...reservationData.unitType,
            };

            const nextReservation = reservationData as MovedInReservation;
            let nextUnitType: UnitType;
            let nextDescriptor: Descriptor;

            if (reservationData.persona) {
                nextUnitType = {
                    ...sampleUnitType,
                    rates: {
                        ...sampleUnitType.rates,
                        rate: reservationData.rates.rate,
                        discounted: reservationData.rates.discounted,
                        discountedRate: reservationData.rates.discountedRate ?? 0,
                    },
                };
                nextDescriptor = {
                    ...sampleDescriptor,
                    name: reservationData.unitType.descriptor.name,
                    tags: reservationData.unitType.descriptor.tags,
                    description: reservationData.unitType.descriptor.description,
                    descriptions:
                        reservationData.unitType.descriptor.descriptions,
                    specialText: reservationData.unitType.descriptor.specialText,
                };
            } else {
                nextUnitType = sampleUnitType;
                nextDescriptor = sampleDescriptor;
            }

            setReservation(nextReservation);
            setUnitType(nextUnitType);
            setDescriptor(nextDescriptor);
            checkCookie(nextReservation, nextDescriptor, nextUnitType);
        }
    }, [
        unitList,
        reservationData,
        setCookie,
        removeCookie,
        token,
        location,
    ]);

    useEffect(() => {
        Logger.info('MoveInConfirmed Screen Loaded', location, [
            {
                name: 'wizard_test',
                value: true,
            },
        ]);
    }, [location]);

    const navigateToIdVerify = () => {
        // TODO: if we ever need to use a URL that's not from MTAS, we'll need to somehow be able to configure the URL and queries (if neede)
        window.location.href = `${window.rapidstor_settings.app_config.journey.idVerify?.url}?email=${reservation?.email}&tenantId=${reservation?.tenantId}`;
    };

    if (window.rapidstor_settings.rapidstor_options.checkoutVariant === 'simplified') {
        return (
            <SimplifiedCheckoutConfirmed
                reservation={reservation}
                reservationDataLoading={reservationDataLoading}
                descriptor={descriptor}
                unitType={unitType}
                corporationData={corporationData}
            />
        );
    }

    return (
        <div className={`${classes.container}`}>
            <div className={`${classes.header}`}>
                <span className={`${classes.title}`} style={{ flexGrow: '1' }}>
                    {t('wizard:scheduleMoveIn')}
                </span>
            </div>

            <WizardDescriptor
                descriptor={descriptor}
                unitType={unitType}
                deal={reservation?.deal ?? undefined}
            />

            <div className={`${classes.fold}`} />

            <div className={`${classes.booking_confirmation}`}>
                {reservationDataLoading ? (
                    <ContentLoader
                        foregroundColor={rapidstorMainColor ?? location.skin.mainColour}
                        backgroundColor="#fff"
                        height="156px"
                        width="100%"
                    >
                        <rect x="0" y="0" rx="5" ry="5" width="100%" height="65px" />
                        <rect x="0" y="95" rx="5" ry="5" width="100%" height="25px" />
                        <rect x="0" y="130" rx="5" ry="5" width="100%" height="25px" />
                    </ContentLoader>
                ) : (
                    <>
                        <div className={`${classes.title}`}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className={`${classes.thumbs_up}`}
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    // eslint-disable-next-line max-len
                                    d="M6.633 10.25c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 0 1 2.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 0 0 .322-1.672V2.75a.75.75 0 0 1 .75-.75 2.25 2.25 0 0 1 2.25 2.25c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282m0 0h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 0 1-2.649 7.521c-.388.482-.987.729-1.605.729H13.48c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 0 0-1.423-.23H5.904m10.598-9.75H14.25M5.904 18.5c.083.205.173.405.27.602.197.4-.078.898-.523.898h-.908c-.889 0-1.713-.518-1.972-1.368a12 12 0 0 1-.521-3.507c0-1.553.295-3.036.831-4.398C3.387 9.953 4.167 9.5 5 9.5h1.053c.472 0 .745.556.5.96a8.958 8.958 0 0 0-1.302 4.665c0 1.194.232 2.333.654 3.375Z"
                                />
                            </svg>

                            <span>{t('wizard:bookingConfirmation:title')}</span>
                        </div>
                        {t('wizard:bookingConfirmation:pleaseWait', { defaultValue: '' }) && (
                            <p className={`${classes.pleaseWait}`}>
                                {t('wizard:bookingConfirmation:pleaseWait')}
                            </p>
                        )}
                        <p className={`${classes.subtitle}`}>
                            {t('wizard:bookingConfirmation:subtitle')}
                        </p>

                        { errorAfterMoveIn && (
                            <Alert type="warning" text={handleAfterMoveInErrorMessage()} />
                        )}
                        <div
                            className={`${classes.referenceCode}`}
                            onClick={copyToClipboard}
                        >
                            <span className={`${classes.text}`}>
                                {t('wizard:bookingConfirmation:referenceCode')}
                            </span>
                            <span className={`${classes.code}`}>
                                {reservation?.globalWaitingNum}
                            </span>
                            {clipBoardIcon}
                        </div>

                        {window.rapidstor_settings.app_config.journey.idVerify?.url && (
                            <div
                                className={`${classes.id_verify_section}`}
                            >
                                <p>{t('wizard:bookingConfirmation:idVerifyText')}</p>
                                <Button
                                    size="small"
                                    type="primary"
                                    onclick={navigateToIdVerify}
                                >
                                    {t('wizard:bookingConfirmation:idVerifyButtonText')}
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </div>

            <div className={`${classes.customer_survey}`}>
                {corporationData?.settings.storApp
                    && corporationData?.settings.storApp.enabled && (
                    <>
                        <div className={`${classes.fold}`} />
                        <span
                            className={`${classes.app_download_text}`}
                            dangerouslySetInnerHTML={{
                                __html: `${corporationData?.settings.storApp.blurbText}`,
                            }}
                        />

                        <div className={`${classes.store_images}`}>
                            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                            <img
                                alt="Google Play"
                                src="https://ik.imagekit.io/bytcx9plm/PlayStore.png?updatedAt=1726538026462"
                                onClick={() => {
                                    window.open(
                                        corporationData?.settings.storApp.links
                                            .googlePlay,
                                        '_blank',
                                    );
                                }}
                            />
                            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                            <img
                                alt="Apple Store"
                                src="https://ik.imagekit.io/bytcx9plm/AppleStore.png?updatedAt=1726538026336"
                                onClick={() => {
                                    window.open(
                                        corporationData?.settings.storApp.links
                                            .iosAppStore,
                                        '_blank',
                                    );
                                }}
                            />
                        </div>
                    </>
                )}

                {(location.settings.marketingSurvey.use && reservation?.tenantId) && (
                    <>
                        <div className={`${classes.fold}`} />
                        <MarketingSurvey
                            marketingSurvey={location.settings.marketingSurvey}
                            tenantId={reservation.tenantId}
                        />
                    </>
                )}
            </div>
        </div>
    );
}
